/**
 * Deletes a single J-code from an order via the backend.
 * @module deleteJCode
 */

import { API_ENDPOINTS } from "../../enums/apiUrls";
import { throwApiError } from "../throwApiError";
import { getBearerToken } from "./bearerToken";

/** Request body for the delete-J-code endpoint. */
export interface DeleteJCodePayload {
  order_id: string;
  org_id: string;
  j_code_id: string;
}

/**
 * POSTs a payload that deletes a single J-code from the given order.
 *
 * Failures follow the `/prior-auth-utilities/*` HTTP-status contract:
 * 4xx/5xx responses throw an `Error` enriched with `status` and `detail`
 * (read by `utils/showApiError` on the consumer side).
 *
 * @param payload - Identifies the order, org, and J-code to delete.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns Parsed JSON response from the server.
 * @throws {Error & { status: number; detail?: string }} On HTTP failure.
 * @throws {Error} On authentication, network, or JSON parse failure.
 */
export const deleteJCode = async (
  payload: DeleteJCodePayload,
  signal?: AbortSignal,
) => {
  try {
    const token = await getBearerToken();
    const response = await fetch(API_ENDPOINTS.DELETE_J_CODE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      ...(signal && { signal }),
    });

    if (!response.ok) {
      await throwApiError(response);
    }
    return await response.json();
  } catch (error) {
    console.error("Failed to delete J-code:", error);
    throw error;
  }
};
